/*
 * SerialInterfacesModal — CRUD + Test für die serielle Bus-Konfiguration
 * eines Field-Clients.
 *
 * Aufbau:
 *   1) Liste der bestehenden SerialInterface-Rows für diesen Client.
 *   2) Add-/Edit-Formular mit den Kotis-Contract-Feldnamen (alias, device,
 *      baudRate, dataBits, stopBits, parity, flowControl, description).
 *   3) Katalog-Vorbelegung: Device-Dropdown pro Client-Produkt-Serial-Port,
 *      plus Freie-Eingabe (für USB-Adapter, umgebrandete Panels).
 *   4) Gegenprobe: Runtime-Info-Serial-Ports mit rx/txBytes wenn vorhanden.
 *   5) Test-Button pro Zeile — passiv (Default) oder aktiv.
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { showToast } from './Toaster';
import type {
  CatalogSerialPort,
  ClientProductDetailDto,
  ClientSerialPortInfo,
  ListSerialInterfacesResponse,
  SerialInterfaceDto,
  SerialInterfaceTestResult,
  UpsertSerialInterfaceRequest,
} from '../api/types';
import './hv-modal.css';

interface Props {
  plantId: string;
  clientId: string;
  clientLabel: string;
  /** Vom Field-Client-Agent gemeldete Ports (aus RuntimeInfo). Optional. */
  runtimePorts?: ClientSerialPortInfo[];
  /** Product-Katalog-Id — Modal lädt die catalog serialPorts selbst nach. */
  productId?: string | null;
  onClose(): void;
}

interface DraftState {
  alias: string;
  device: string;
  baudRate: number;
  dataBits: number;
  stopBits: 1 | 2;
  parity: 'none' | 'even' | 'odd';
  flowControl: '' | 'none' | 'rts-cts' | 'xon-xoff';
  description: string;
}

const emptyDraft: DraftState = {
  alias: '',
  device: '',
  baudRate: 9600,
  dataBits: 8,
  stopBits: 1,
  parity: 'none',
  flowControl: '',
  description: '',
};

export function SerialInterfacesModal({
  plantId, clientId, clientLabel, runtimePorts, productId, onClose,
}: Props) {
  const [refreshTick, setRefreshTick] = useState(0);
  const listState = useAsync(
    (signal) => api.get<ListSerialInterfacesResponse>(
      `/api/plants/${plantId}/clients/${clientId}/serial-interfaces`, signal),
    [plantId, clientId, refreshTick],
  );

  // Katalog-Ports vom ClientProduct nachladen falls verknuepft. Fehler still
  // ignorieren — Modal funktioniert auch ohne Katalog, freie Device-Eingabe.
  const productState = useAsync(
    (signal) => productId
      ? api.get<ClientProductDetailDto>(`/api/client-catalog/products/${productId}`, signal)
      : Promise.resolve<ClientProductDetailDto | null>(null),
    [productId],
  );
  const catalogPorts = useMemo<CatalogSerialPort[]>(() => {
    if (productState.status !== 'ok' || !productState.data) return [];
    try {
      const parsed = JSON.parse(productState.data.serialPortsJson);
      return Array.isArray(parsed) ? (parsed as CatalogSerialPort[]) : [];
    } catch {
      return [];
    }
  }, [productState]);

  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [editingAlias, setEditingAlias] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ alias: string; result: SerialInterfaceTestResult } | null>(null);

  const rows = listState.status === 'ok' ? listState.data.items : [];

  const catalogDeviceOptions = catalogPorts;
  const runtimeDeviceMap = useMemo(() => {
    const m = new Map<string, ClientSerialPortInfo>();
    for (const p of runtimePorts ?? []) m.set(p.device, p);
    return m;
  }, [runtimePorts]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [busy, onClose]);

  const startEdit = (row: SerialInterfaceDto) => {
    setDraft({
      alias: row.alias,
      device: row.device,
      baudRate: row.baudRate,
      dataBits: row.dataBits,
      stopBits: (row.stopBits === 2 ? 2 : 1),
      parity: row.parity,
      flowControl: row.flowControl ?? '',
      description: row.description ?? '',
    });
    setEditingAlias(row.alias);
    setError(null);
  };

  const resetDraft = () => {
    setDraft(emptyDraft);
    setEditingAlias(null);
    setError(null);
  };

  const submit = async () => {
    if (!draft.alias.trim()) { setError('Alias fehlt.'); return; }
    if (!draft.device.trim()) { setError('Device fehlt (z.B. /dev/ttyS0).'); return; }
    const body: UpsertSerialInterfaceRequest = {
      device: draft.device.trim(),
      baudRate: draft.baudRate,
      dataBits: draft.dataBits,
      stopBits: draft.stopBits,
      parity: draft.parity,
      flowControl: draft.flowControl === '' ? null : draft.flowControl,
      description: draft.description.trim() || null,
    };
    setBusy(true);
    setError(null);
    try {
      await api.put<SerialInterfaceDto>(
        `/api/plants/${plantId}/clients/${clientId}/serial-interfaces/${encodeURIComponent(draft.alias.trim())}`,
        body,
      );
      showToast({
        tone: 'success',
        title: editingAlias ? 'Serial-Interface aktualisiert' : 'Serial-Interface angelegt',
        message: `${draft.alias} → ${draft.device}, ${draft.baudRate} ${draft.dataBits}${draft.parity[0].toUpperCase()}${draft.stopBits}`,
        autoHideAfterMs: 3000,
      });
      resetDraft();
      setRefreshTick((n) => n + 1);
    } catch (e) {
      if (e instanceof ApiError) {
        setError((e.body as { error?: string })?.error ?? `Fehler ${e.status}`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler.');
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (alias: string) => {
    if (!window.confirm(`Serial-Interface "${alias}" löschen?`)) return;
    setBusy(true);
    try {
      await api.delete<void>(
        `/api/plants/${plantId}/clients/${clientId}/serial-interfaces/${encodeURIComponent(alias)}`,
      );
      showToast({ tone: 'success', title: 'Gelöscht', message: `Serial-Interface "${alias}"`, autoHideAfterMs: 2500 });
      if (editingAlias === alias) resetDraft();
      setRefreshTick((n) => n + 1);
    } catch (e) {
      showToast({ tone: 'error', title: 'Löschen fehlgeschlagen', message: describeError(e), autoHideAfterMs: 5000 });
    } finally {
      setBusy(false);
    }
  };

  const test = async (alias: string, mode: 'passive' | 'active') => {
    setBusy(true);
    setTestResult(null);
    try {
      const result = await api.post<SerialInterfaceTestResult>(
        `/api/plants/${plantId}/clients/${clientId}/serial-interfaces/${encodeURIComponent(alias)}/test`,
        { mode, observeSeconds: 5 },
      );
      setTestResult({ alias, result });
    } catch (e) {
      showToast({ tone: 'error', title: 'Test fehlgeschlagen', message: describeError(e), autoHideAfterMs: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !busy && onClose()}>
      <div class="hv-modal hv-modal--serial-interfaces" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Serielle Schnittstellen · {clientLabel}</h2>
          <button type="button" class="hv-modal__close" onClick={onClose}
                  aria-label="Schließen" disabled={busy}>×</button>
        </div>
        <div class="hv-modal__body" style={{ display: 'grid', gap: 16 }}>

          {/* Katalog + Runtime — Kompakte Übersicht als Gegenprobe */}
          <section>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Verfügbare Ports (Modell + Live-Meldung)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {catalogDeviceOptions.length === 0 && (runtimePorts?.length ?? 0) === 0 && (
                <span class="muted">Weder Katalog-Ports gepflegt noch Runtime-Meldung. Freie Eingabe unten.</span>
              )}
              {catalogDeviceOptions.map((p) => {
                const live = runtimeDeviceMap.get(p.physical);
                return (
                  <span key={p.physical} class="hv-serial-chip">
                    <strong>{p.name}</strong>
                    <span class="mono muted" style={{ marginLeft: 6 }}>{p.physical}</span>
                    <span class="muted" style={{ marginLeft: 6 }}>{p.kind}</span>
                    {live && (
                      <span class="muted" style={{ marginLeft: 8, fontSize: '0.75rem' }}>
                        rx {live.rxBytes ?? '?'} · tx {live.txBytes ?? '?'}
                      </span>
                    )}
                    {live?.console && (
                      <span class="hv-serial-chip__warn" style={{ marginLeft: 6 }}>console!</span>
                    )}
                  </span>
                );
              })}
              {(runtimePorts ?? [])
                .filter((p) => !catalogDeviceOptions.some((c) => c.physical === p.device))
                .map((p) => (
                  <span key={p.device} class="hv-serial-chip hv-serial-chip--extra">
                    <strong>{p.name}</strong>
                    <span class="mono muted" style={{ marginLeft: 6 }}>{p.device}</span>
                    {p.role && <span class="muted" style={{ marginLeft: 6 }}>Rolle {p.role}?</span>}
                    <span class="muted" style={{ marginLeft: 8, fontSize: '0.75rem' }}>
                      rx {p.rxBytes ?? '?'} · tx {p.txBytes ?? '?'}
                    </span>
                    {p.console && <span class="hv-serial-chip__warn" style={{ marginLeft: 6 }}>console!</span>}
                  </span>
                ))}
            </div>
          </section>

          {/* Liste bestehender SerialInterface-Rows */}
          <section>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Konfigurierte Aliase</div>
            {listState.status === 'loading' && <span class="muted">Lade…</span>}
            {rows.length === 0 && listState.status === 'ok' && (
              <p class="muted">Noch keine konfiguriert. Formular unten nutzen.</p>
            )}
            {rows.map((row) => {
              const live = runtimeDeviceMap.get(row.device);
              return (
                <div key={row.id} class="hv-serial-row">
                  <div class="hv-serial-row__main">
                    <strong>{row.alias}</strong>
                    <span class="mono muted" style={{ marginLeft: 8 }}>{row.device}</span>
                    <span class="muted" style={{ marginLeft: 8 }}>
                      {row.baudRate} {row.dataBits}{row.parity[0].toUpperCase()}{row.stopBits}
                    </span>
                    {row.description && (
                      <span class="muted" style={{ marginLeft: 8, fontStyle: 'italic' }}>{row.description}</span>
                    )}
                    {live && (
                      <span class="muted" style={{ marginLeft: 8, fontSize: '0.75rem' }}>
                        rx {live.rxBytes ?? '?'} · tx {live.txBytes ?? '?'}
                      </span>
                    )}
                  </div>
                  <div class="hv-serial-row__actions">
                    <button type="button" class="hv-btn hv-btn--sm"
                            onClick={() => test(row.alias, 'passive')} disabled={busy}
                            title="Passiver Test — beobachtet 5s rx/txBytes-Delta, öffnet den Port nicht. Achtung: Modbus-Slaves senden nie unaufgefordert, 0 Bytes passiv sind daher KEIN Befund.">
                      Test (passiv)
                    </button>
                    <button type="button" class="hv-btn hv-btn--sm"
                            onClick={() => test(row.alias, 'active')} disabled={busy}
                            title="Aktiver Test — öffnet den Port kurz + fragt den Slave an. Kann einen laufenden Bus stören.">
                      Test (aktiv)
                    </button>
                    <button type="button" class="hv-btn hv-btn--sm" onClick={() => startEdit(row)} disabled={busy}>
                      Bearbeiten
                    </button>
                    <button type="button" class="hv-btn hv-btn--sm hv-btn--danger-outline"
                            onClick={() => remove(row.alias)} disabled={busy}>
                      Löschen
                    </button>
                  </div>
                </div>
              );
            })}
            {testResult && (
              <div class={`hv-serial-testresult hv-serial-testresult--${testResult.result.status}`}
                   style={{ marginTop: 8 }}>
                <strong>{testResult.alias}</strong>: {testResult.result.status}
                {testResult.result.resolvedDevice && <> · {testResult.result.resolvedDevice}</>}
                {testResult.result.latencyMs !== null && <> · {testResult.result.latencyMs} ms</>}
                {testResult.result.rxBytesDelta !== null && <> · rx +{testResult.result.rxBytesDelta}</>}
                {testResult.result.txBytesDelta !== null && <> · tx +{testResult.result.txBytesDelta}</>}
                {testResult.result.validFrames !== null && testResult.result.validFrames !== undefined && (
                  <> · {testResult.result.validFrames} valide Frames</>
                )}
                {testResult.result.status === 'crc-error' && (
                  <div class="muted" style={{ marginTop: 4 }}>
                    Bytes kommen an, Prüfsumme scheitert. Deutet auf Leitungs- oder Treiber-Konfig
                    (termios ixon/istrip, Baud/Parity), nicht auf Verkabelung.
                  </div>
                )}
                {testResult.result.error && <div class="muted">{testResult.result.error}</div>}
              </div>
            )}
          </section>

          {/* Add-/Edit-Formular */}
          <section>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {editingAlias ? `Bearbeiten: ${editingAlias}` : 'Neuer Bus'}
            </div>
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <label class="hv-serial-field">
                <span>Alias (COM1, Heizkreis-Bus)</span>
                <input type="text" value={draft.alias} disabled={!!editingAlias || busy}
                       onInput={(e) => setDraft({ ...draft, alias: (e.currentTarget as HTMLInputElement).value })} />
              </label>
              <label class="hv-serial-field">
                <span>Device</span>
                {catalogDeviceOptions.length > 0 ? (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <select value={draft.device} disabled={busy}
                            onChange={(e) => setDraft({ ...draft, device: (e.currentTarget as HTMLSelectElement).value })}
                            style={{ flex: 1 }}>
                      <option value="">(freie Eingabe)</option>
                      {catalogDeviceOptions.map((p) => (
                        <option key={p.physical} value={p.physical}>{p.name} — {p.physical}</option>
                      ))}
                    </select>
                    <input type="text" value={draft.device} disabled={busy}
                           placeholder="/dev/ttyUSB0"
                           style={{ flex: 1 }}
                           onInput={(e) => setDraft({ ...draft, device: (e.currentTarget as HTMLInputElement).value })} />
                  </div>
                ) : (
                  <input type="text" value={draft.device} disabled={busy}
                         placeholder="/dev/ttyS0"
                         onInput={(e) => setDraft({ ...draft, device: (e.currentTarget as HTMLInputElement).value })} />
                )}
              </label>
              <label class="hv-serial-field">
                <span>Baud</span>
                <input type="number" min={300} max={1000000} value={draft.baudRate} disabled={busy}
                       onInput={(e) => setDraft({ ...draft, baudRate: parseInt((e.currentTarget as HTMLInputElement).value, 10) || 9600 })} />
              </label>
              <label class="hv-serial-field">
                <span>Data Bits</span>
                <select value={draft.dataBits} disabled={busy}
                        onChange={(e) => setDraft({ ...draft, dataBits: parseInt((e.currentTarget as HTMLSelectElement).value, 10) })}>
                  <option value={5}>5</option><option value={6}>6</option>
                  <option value={7}>7</option><option value={8}>8</option>
                </select>
              </label>
              <label class="hv-serial-field">
                <span>Stop Bits</span>
                <select value={draft.stopBits} disabled={busy}
                        onChange={(e) => setDraft({ ...draft, stopBits: parseInt((e.currentTarget as HTMLSelectElement).value, 10) as 1 | 2 })}>
                  <option value={1}>1</option><option value={2}>2</option>
                </select>
              </label>
              <label class="hv-serial-field">
                <span>Parity</span>
                <select value={draft.parity} disabled={busy}
                        onChange={(e) => setDraft({ ...draft, parity: (e.currentTarget as HTMLSelectElement).value as 'none' | 'even' | 'odd' })}>
                  <option value="none">none</option><option value="even">even</option><option value="odd">odd</option>
                </select>
              </label>
              <label class="hv-serial-field">
                <span>Flow Control (optional)</span>
                <select value={draft.flowControl} disabled={busy}
                        onChange={(e) => setDraft({ ...draft, flowControl: (e.currentTarget as HTMLSelectElement).value as DraftState['flowControl'] })}>
                  <option value="">(default)</option>
                  <option value="none">none</option>
                  <option value="rts-cts">rts-cts (nur Linux)</option>
                  <option value="xon-xoff">xon-xoff (nur Linux)</option>
                </select>
              </label>
              <label class="hv-serial-field" style={{ gridColumn: '1 / -1' }}>
                <span>Beschreibung</span>
                <input type="text" value={draft.description} disabled={busy}
                       placeholder="z.B. Kessel Nord"
                       onInput={(e) => setDraft({ ...draft, description: (e.currentTarget as HTMLInputElement).value })} />
              </label>
            </div>
            {error && <p class="hv-modal__error" style={{ marginTop: 8 }}>{error}</p>}
          </section>

        </div>
        <div class="hv-modal__footer">
          <div class="hv-modal__footer-spacer" />
          {editingAlias && (
            <button type="button" class="hv-btn" onClick={resetDraft} disabled={busy}>
              Neuer Bus
            </button>
          )}
          <button type="button" class="hv-btn" onClick={onClose} disabled={busy}>Schließen</button>
          <button type="button" class="hv-btn hv-btn--primary" onClick={submit} disabled={busy}>
            {editingAlias ? 'Speichern' : 'Anlegen'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function describeError(e: unknown): string {
  if (e instanceof ApiError) return (e.body as { error?: string })?.error ?? `Fehler ${e.status}`;
  if (e instanceof Error) return e.message;
  return 'Unbekannter Fehler.';
}
